import { Box, Flex, Grid, GridItem, Skeleton, Text } from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';

import {
  highlightComputedStatuses,
  HighlightComputedStatus,
  InvoiceWithComputedStatus,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { dollarNumberWithCommas } from 'packages/common-frontend/utils';

type InvoicesOverviewCardProps = {
  label: string;
  totalAmount: number;
  totalCount: number;
  icon: React.ReactElement;
  isSelected: boolean;
  onClick: () => void;
};
const InvoicesOverviewCard = ({
  label,
  totalAmount,
  totalCount,
  icon,
  isSelected,
  onClick,
}: InvoicesOverviewCardProps) => {
  return (
    <Flex
      alignItems="center"
      p="24px"
      borderRadius="8px"
      borderColor={isSelected ? colors.black[95] : colors.gray[100]}
      borderWidth="1px"
      onClick={onClick}
    >
      <Box mr="24px">{icon}</Box>
      <Box>
        <Text color={colors.black[70]} mb="4px">
          {label}
        </Text>

        <Text fontWeight={600} fontSize="24px" mb="4px">
          {dollarNumberWithCommas(totalAmount)}
        </Text>

        <Text color={colors.black[70]}>{`${totalCount} invoice${
          totalCount === 1 ? '' : 's'
        }`}</Text>
      </Box>
    </Flex>
  );
};

type InvoicesOverviewCardSkeletonProps = {
  label: string;
  icon: React.ReactElement;
};
const InvoicesOverviewCardSkeleton = ({
  label,
  icon,
}: InvoicesOverviewCardSkeletonProps) => {
  return (
    <Flex
      alignItems="center"
      p="24px"
      borderRadius="8px"
      borderColor={colors.gray[100]}
      borderWidth="1px"
    >
      <Box mr="24px">{icon}</Box>
      <Box>
        <Text color={colors.black[70]} mb="4px">
          {label}
        </Text>

        <Skeleton h="32px" w="200px" mb="4px" />
        <Skeleton h="20px" w="80px" />
      </Box>
    </Flex>
  );
};

const InvoicesOverviewCardsSkeleton = () => {
  return (
    <Grid templateColumns="repeat(3, 1fr)" gap="24px" cursor="default">
      <GridItem>
        <InvoicesOverviewCardSkeleton
          label="Due Soon"
          icon={<Icon icon="calendar-clock" color={colors.orange[400]} />}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCardSkeleton
          label="Overdue"
          icon={<Icon icon="calendar-clock" color={colors.red[400]} />}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCardSkeleton
          label="Paid"
          icon={<Icon icon="circle-check" color={colors.green[400]} />}
        />
      </GridItem>
    </Grid>
  );
};

type InvoicesOverviewCardsWithDataProps = {
  invoices: InvoiceWithComputedStatus[];
  selectedComputedStatus: HighlightComputedStatus | null;
  setSelectedComputedStatus: React.Dispatch<
    React.SetStateAction<HighlightComputedStatus | null>
  >;
};
const InvoicesOverviewCardsWithData = ({
  invoices,
  selectedComputedStatus,
  setSelectedComputedStatus,
}: InvoicesOverviewCardsWithDataProps) => {
  const invoiceAggregates = useMemo(() => {
    const aggregatesForHighlightStatuses = invoices.reduce(
      (acc, invoice) => {
        const computedStatus = invoice.computedStatus;
        if (highlightComputedStatuses.includes(computedStatus)) {
          return {
            ...acc,
            [computedStatus]: {
              totalAmount:
                acc[computedStatus].totalAmount + invoice.totalAmount,
              totalCount: acc[computedStatus].totalCount + 1,
            },
          };
        }

        return acc;
      },
      // we are deciding to only show these 3 statuses as cards
      // to highlight the money that is coming in
      {
        dueSoon: { totalAmount: 0, totalCount: 0 },
        overdue: { totalAmount: 0, totalCount: 0 },
        paid: { totalAmount: 0, totalCount: 0 },
      } as Record<
        HighlightComputedStatus,
        { totalAmount: number; totalCount: number }
      >,
    );

    return aggregatesForHighlightStatuses;
  }, [invoices]);

  const onClickOverviewStatusCard = useCallback(
    (computedStatus: HighlightComputedStatus) => {
      if (computedStatus === selectedComputedStatus) {
        setSelectedComputedStatus(null);
      } else {
        setSelectedComputedStatus(computedStatus);
      }
    },
    [selectedComputedStatus, setSelectedComputedStatus],
  );

  return (
    <Grid templateColumns="repeat(3, 1fr)" gap="24px" cursor="pointer">
      <GridItem>
        <InvoicesOverviewCard
          label="Due Soon"
          totalAmount={invoiceAggregates.dueSoon.totalAmount}
          totalCount={invoiceAggregates.dueSoon.totalCount}
          icon={<Icon icon="calendar-clock" color={colors.orange[400]} />}
          isSelected={selectedComputedStatus === 'dueSoon'}
          onClick={() => onClickOverviewStatusCard('dueSoon')}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCard
          label="Overdue"
          totalAmount={invoiceAggregates.overdue.totalAmount}
          totalCount={invoiceAggregates.overdue.totalCount}
          icon={<Icon icon="calendar-clock" color={colors.red[400]} />}
          isSelected={selectedComputedStatus === 'overdue'}
          onClick={() => onClickOverviewStatusCard('overdue')}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCard
          label="Paid"
          totalAmount={invoiceAggregates.paid.totalAmount}
          totalCount={invoiceAggregates.paid.totalCount}
          icon={<Icon icon="circle-check" color={colors.green[400]} />}
          isSelected={selectedComputedStatus === 'paid'}
          onClick={() => onClickOverviewStatusCard('paid')}
        />
      </GridItem>
    </Grid>
  );
};

type InvoicesOverviewCardsProps = {
  invoices: InvoiceWithComputedStatus[] | undefined;
  isLoading: boolean;
  selectedComputedStatus: HighlightComputedStatus | null;
  setSelectedComputedStatus: React.Dispatch<
    React.SetStateAction<HighlightComputedStatus | null>
  >;
};
export const InvoicesOverviewCards = ({
  invoices,
  isLoading,
  selectedComputedStatus,
  setSelectedComputedStatus,
}: InvoicesOverviewCardsProps) => {
  if (isLoading || !invoices) {
    return <InvoicesOverviewCardsSkeleton />;
  }

  return (
    <InvoicesOverviewCardsWithData
      invoices={invoices}
      selectedComputedStatus={selectedComputedStatus}
      setSelectedComputedStatus={setSelectedComputedStatus}
    />
  );
};
